/**
 * `useNotificationCenter` — ciclo de vida completo del centro de
 * notificaciones (ADR 0022, fase 4): carga inicial por REST, conexión al
 * WebSocket con reconexión + catch-up, y las tres mutaciones (leer/ocultar/
 * restaurar) con actualización local optimista.
 *
 * El cableado fino de UI (toasts, navegación al hacer click en una fila)
 * queda en `NotificationBell.tsx` — este hook no importa nada de `sonner` ni
 * decide adónde navega el click; sólo expone datos y acciones.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Por qué no hay un flag `enabled`
 * ═══════════════════════════════════════════════════════════════════════════
 * El componente que usa este hook (`NotificationBell`) sólo se monta cuando
 * YA hay sesión — tanto `App.tsx` como `AdminShell.tsx` retornan temprano
 * (`LoginForm` / pantalla "sin acceso") antes de llegar al header/sidebar
 * donde vive la campana. Eso significa que el ciclo de vida del hook es
 * simplemente mount→unmount: nunca hay una sesión que aparece o desaparece
 * DEBAJO de un componente que sigue montado. Consecuencia útil: "cerrar el
 * socket al hacer logout" no necesita cableado propio — el logout ya
 * desmonta la campana (mismo mecanismo que corta cualquier otro estado de
 * componente), y el efecto de este hook cierra el socket en su cleanup. Ver
 * el reporte de la fase 4 para la traza completa de esta decisión.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Degradación sin DO (ADR 0022 §8)
 * ═══════════════════════════════════════════════════════════════════════════
 * Si `GET /stream` no upgradea (binding ausente → 503, o cualquier otro
 * fallo de red), el navegador dispara `onerror` seguido de `onclose` sobre
 * el WebSocket recién creado. Este hook trata eso EXACTAMENTE igual que
 * cualquier otra caída: agenda un reintento con backoff. El REST (`load`)
 * ya corrió aparte y no depende del socket — el centro se ve y funciona,
 * sólo sin push en vivo. `connectionPhase` nunca queda en `"open"`, así que
 * la UI muestra el estado real ("actualizado hace X") en vez de mentir con
 * "En vivo".
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Modo "Ocultas" — fetch perezoso, mergeado en la misma lista
 * ═══════════════════════════════════════════════════════════════════════════
 * `load()` (montaje) sólo trae `?hidden=excluded` (default del servidor): la
 * carga inicial de la campana no necesita saber nunca que existe una bandeja
 * de ocultas. Esa bandeja se trae recién cuando el toggle del panel pide
 * `mode="hidden"` por primera vez (`loadHidden`, `GET ?hidden=only`) — evita
 * un segundo roundtrip en cada apertura del panel cuando nadie mira
 * "Ocultas". El resultado se MERGEA en la misma lista `all` (no un estado
 * separado): dedupe por `id` vía `mergeNotificationLists`, así que ocultar
 * algo mientras se mira la bandeja normal y después abrir "Ocultas" no trae
 * un duplicado, y des-ocultar algo actualiza `hiddenAt` in-place sin pedir
 * nada de nuevo al servidor. `hiddenLoaded` evita repetir el fetch en cada
 * toggle; `refresh()` lo repite si la vista activa es "Ocultas".
 *
 * Por qué un push del WS no ensucia la vista de ocultas mientras está
 * abierta: el sub-protocolo (`fromPushPayload`) garantiza que una
 * notificación recién empujada SIEMPRE llega con `hiddenAt: null` — no puede
 * ser otra cosa en el instante en que se crea. El selector de "Ocultas"
 * (`hiddenNotifications`, `notification-policy.ts`) filtra por
 * `hiddenAt !== null`. No hace falta ningún chequeo de modo dentro de
 * `ws.onmessage`: el filtro por dato ya alcanza, y es más simple que
 * bifurcar el reducer del stream según qué esté mirando el usuario en ese
 * instante.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { signOut } from "../auth-client";
import {
  fetchNotifications,
  getApiBaseUrl,
  hideNotification as hideNotificationApi,
  markNotificationRead as markNotificationReadApi,
  unhideNotification as unhideNotificationApi,
} from "./notification-api";
import {
  buildStreamUrl,
  computeBackoffDelayMs,
  computeSinceId,
  countUnread,
  hiddenNotifications,
  markLocalHidden,
  markLocalRead,
  markLocalUnhidden,
  mergeNotificationLists,
  parseServerStreamFrame,
  reduceStreamFrame,
  visibleNotifications,
} from "./notification-policy";
import type { ConnectionPhase, NotificationDTO, NotificationViewMode } from "./types";

export interface UseNotificationCenterResult {
  /**
   * Filtradas según `mode` — visibles (sin las ocultadas) por default, o
   * solo las ocultadas cuando `mode === "hidden"`. Orden `createdAt` desc.
   */
  notifications: NotificationDTO[];
  /** Vista activa del panel — ver docstring §Modo "Ocultas". */
  mode: NotificationViewMode;
  /** Cambia la vista activa; dispara el fetch perezoso de ocultas si hace falta. */
  setMode: (mode: NotificationViewMode) => void;
  /** No leídas Y visibles — no cambia con `mode` (una oculta nunca suma, mirándola o no). */
  unreadCount: number;
  connectionPhase: ConnectionPhase;
  lastSyncAt: Date | null;
  /** Loading de la lista de la vista ACTIVA (`mode`), no de ambas a la vez. */
  loading: boolean;
  error: string | null;
  markRead: (id: string) => Promise<void>;
  hide: (id: string) => Promise<void>;
  unhide: (id: string) => Promise<void>;
  /** Recarga la vista activa (`mode`). */
  refresh: () => Promise<void>;
}

export function useNotificationCenter(): UseNotificationCenterResult {
  const [all, setAll] = useState<NotificationDTO[]>([]);
  const [connectionPhase, setConnectionPhase] = useState<ConnectionPhase>("connecting");
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modo "Ocultas" — ver docstring del archivo. `hiddenLoaded` evita
  // repetir `GET ?hidden=only` en cada toggle de vuelta a "hidden".
  const [mode, setModeState] = useState<NotificationViewMode>("visible");
  const [hiddenLoaded, setHiddenLoaded] = useState(false);
  const [loadingHidden, setLoadingHidden] = useState(false);

  const navigate = useNavigate();

  // Refs para el ciclo de vida del socket — no deben disparar re-render.
  const allRef = useRef<NotificationDTO[]>(all);
  allRef.current = all;
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  const everConnectedRef = useRef(false);
  // true mientras el cierre es intencional (unmount) — evita que `onclose`
  // agende una reconexión después de que el componente ya se desmontó.
  const closingRef = useRef(false);

  // `applyMode` (nombre a propósito distinto de la `mode` de vista del panel
  // — no es lo mismo "reemplazar vs. mergear la lista local" que "visible vs.
  // ocultas"): "replace" para la carga inicial (bandeja normal, reemplaza
  // `all`), "merge" para catch-up y para `loadHidden` (agrega sin pisar).
  const applyFetchedList = useCallback(
    (rows: NotificationDTO[], applyMode: "replace" | "merge") => {
      setAll((current) => (applyMode === "replace" ? rows : mergeNotificationLists(current, rows)));
      setLastSyncAt(new Date());
    },
    [],
  );

  const load = useCallback(async () => {
    setLoadingVisible(true);
    setError(null);
    try {
      const rows = await fetchNotifications({});
      applyFetchedList(rows, "replace");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar notificaciones");
    } finally {
      setLoadingVisible(false);
    }
  }, [applyFetchedList]);

  /**
   * Fetch perezoso de la vista "Ocultas" (`GET ?hidden=only`) — se dispara
   * la primera vez que el toggle pide `mode="hidden"` (`setMode`) o cuando
   * `refresh()` corre con esa vista activa. Merge, no replace: no debe pisar
   * lo que `load()` ya trajo de la bandeja normal.
   */
  const loadHidden = useCallback(async () => {
    setLoadingHidden(true);
    setError(null);
    try {
      const rows = await fetchNotifications({ hidden: "only" });
      applyFetchedList(rows, "merge");
      setHiddenLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar notificaciones ocultas");
    } finally {
      setLoadingHidden(false);
    }
  }, [applyFetchedList]);

  /** Cambia la vista activa; trae la bandeja de ocultas la primera vez que se pide. */
  const setMode = useCallback(
    (next: NotificationViewMode) => {
      setModeState(next);
      if (next === "hidden" && !hiddenLoaded) {
        void loadHidden();
      }
    },
    [hiddenLoaded, loadHidden],
  );

  /** ADR 0022 §4 — al reconectar, `GET ?since=<lastSeenId>` para no perder nada. */
  const catchUp = useCallback(async () => {
    try {
      const since = computeSinceId(allRef.current);
      const rows = await fetchNotifications(since ? { since } : {});
      applyFetchedList(rows, "merge");
    } catch (err) {
      // No es crítico: el próximo `GET /api/notifications` sin cursor (al
      // reabrir el panel, ver `refresh`) vuelve a poner todo al día.
      console.error("[notifications] catch-up tras reconexión falló", err);
    }
  }, [applyFetchedList]);

  /**
   * Defensa en profundidad (ADR 0022 §4): el corte real ya lo hace el
   * servidor cerrando el WS (`closeSession`, código 4401). Acá solo evitamos
   * que un cliente que ignore el frame se quede con un socket abierto y una
   * sesión visualmente "activa" — se cierra el socket y se navega a login.
   */
  const handleSessionRevoked = useCallback(() => {
    closingRef.current = true;
    socketRef.current?.close(1000, "session_revoked");
    socketRef.current = null;
    signOut()
      .then(() => navigate("/", { replace: true }))
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  const connect = useCallback(() => {
    if (typeof WebSocket === "undefined") return;
    setConnectionPhase("connecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(buildStreamUrl(getApiBaseUrl()));
    } catch {
      // URL inválida o WS bloqueado por el entorno — degradación graceful,
      // el REST ya cargó por separado.
      setConnectionPhase("closed");
      return;
    }
    socketRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setConnectionPhase("open");
      if (everConnectedRef.current) {
        // Reconexión (no la primera apertura) → catch-up (ADR 0022 §4).
        void catchUp();
      }
      everConnectedRef.current = true;
    };

    ws.onmessage = (event) => {
      let raw: unknown;
      try {
        raw = JSON.parse(String(event.data));
      } catch {
        return;
      }
      const frame = parseServerStreamFrame(raw);
      if (!frame) return;

      const { list, sessionRevoked } = reduceStreamFrame(allRef.current, frame);
      if (frame.type === "notification") {
        setAll(list);
        setLastSyncAt(new Date());
      }
      if (sessionRevoked) handleSessionRevoked();
    };

    ws.onclose = () => {
      socketRef.current = null;
      setConnectionPhase("closed");
      if (closingRef.current) return;
      const delay = computeBackoffDelayMs(attemptRef.current);
      attemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    // El navegador dispara `close` después de `error` — la reconexión ya
    // queda agendada en `onclose`, nada más que hacer acá salvo loguear.
    ws.onerror = () => {
      console.warn("[notifications] error de WebSocket, esperando close para reintentar");
    };
  }, [catchUp, handleSessionRevoked]);

  // Solo al montar/desmontar: `load`/`connect` son estables (useCallback con
  // sus propias deps) y este hook vive todo el tiempo montado que dura la
  // sesión (ver docstring del archivo) — no hay un `enabled` que cambie a
  // mitad de vida y deba re-disparar el ciclo.
  // biome-ignore lint/correctness/useExhaustiveDependencies: efecto de ciclo de vida único (mount/unmount) — load/connect son estables via useCallback y no deben re-suscribir el WS en cada render.
  useEffect(() => {
    closingRef.current = false;
    void load();
    connect();
    return () => {
      closingRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      socketRef.current?.close(1000, "unmount");
      socketRef.current = null;
    };
  }, []);

  const markRead = useCallback(async (id: string) => {
    setAll((current) => markLocalRead(current, id, new Date().toISOString()));
    try {
      await markNotificationReadApi(id);
    } catch (err) {
      // Optimista sin rollback: en el peor caso el próximo refresh corrige
      // el estado. Marcar como vista no tiene efecto destructivo que revertir.
      console.error("[notifications] markRead falló", err);
    }
  }, []);

  const hide = useCallback(async (id: string) => {
    setAll((current) => markLocalHidden(current, id, new Date().toISOString()));
    try {
      await hideNotificationApi(id);
    } catch (err) {
      console.error("[notifications] hide falló", err);
      setAll((current) => markLocalUnhidden(current, id));
      throw err;
    }
  }, []);

  const unhide = useCallback(async (id: string) => {
    setAll((current) => markLocalUnhidden(current, id));
    try {
      await unhideNotificationApi(id);
    } catch (err) {
      console.error("[notifications] unhide falló", err);
      throw err;
    }
  }, []);

  /** Recarga la vista activa — la normal, o "Ocultas" si es la que se está mirando. */
  const refresh = useCallback(async () => {
    if (mode === "hidden") {
      await loadHidden();
    } else {
      await load();
    }
  }, [mode, load, loadHidden]);

  return {
    notifications: mode === "hidden" ? hiddenNotifications(all) : visibleNotifications(all),
    mode,
    setMode,
    unreadCount: countUnread(all),
    connectionPhase,
    lastSyncAt,
    loading: mode === "hidden" ? loadingHidden : loadingVisible,
    error,
    markRead,
    hide,
    unhide,
    refresh,
  };
}
